import { useContext, useEffect, useRef } from "react"
import { fetchArchivePage } from "@/api/xeps/MAM"
import { parseForeignChatLastEvent } from "@/lib/foreignChatLastEvent"
import { useAppDispatch, useAppStore } from "@/reducers/hooks"
import {
  foreignActivityDenied,
  foreignActivityRequested,
  setForeignActivity,
} from "@/reducers/foreignChatActivitySlice"
import { XmppContext } from "@/stream/xmppClient"

const CONCURRENCY = 4

/** When a dispatcher's section opens, ask each of their rooms for its last
 * archived message. One query per room per session, and never more than four in
 * flight for the whole list: every expansion feeds one shared queue, so opening
 * a second section while the first is still loading adds work, not workers.
 * Reading a room's archive needs no presence in it — `admin` is enough. */
export const useForeignChatActivity = (chatJids: string[]): void => {
  const dispatch = useAppDispatch()
  const store = useAppStore()
  const { client } = useContext(XmppContext)

  // One queue and one in-flight count for the whole session. Nothing is
  // cancelled on cleanup: a room is asked for once, so dropping an answer
  // mid-flight would leave that room stuck on "loading" with no second chance.
  const queueRef = useRef<string[]>([])
  const inFlightRef = useRef(0)

  const chatJidsKey = chatJids.join(",")

  useEffect(() => {
    if (!client) return

    // Read from the store rather than through a selector: this hook only needs
    // the answers to decide what to ask for, and subscribing the chat list to a
    // slice that changes on every answer would re-render it once per room.
    const known = store.getState().foreignChatActivity
    const pending = chatJids.filter((jid) => !known[jid])
    if (pending.length === 0) return

    // Marking them up front is what keeps a room from being asked twice, even
    // while its answer is still on the way.
    dispatch(foreignActivityRequested(pending))
    queueRef.current.push(...pending)

    const drain = async () => {
      try {
        for (
          let jid = queueRef.current.shift();
          jid;
          jid = queueRef.current.shift()
        ) {
          try {
            const { accumulator } = await fetchArchivePage(client, {
              toJid: jid,
              max: "1",
              before: true,
            })
            const last = accumulator[accumulator.length - 1]
            const { stamp, event } = last
              ? parseForeignChatLastEvent(last, client.jid?.bare().toString())
              : { stamp: undefined, event: undefined }
            dispatch(
              setForeignActivity({
                jid,
                lastMessageAt: stamp,
                lastEvent: event,
              }),
            )
          } catch {
            // Most often `forbidden`: the room has no office account inside, so
            // its archive is closed to us. The row says so instead of staying
            // blank.
            dispatch(foreignActivityDenied(jid))
          }
        }
      } finally {
        inFlightRef.current -= 1
      }
    }

    const toStart = Math.min(
      CONCURRENCY - inFlightRef.current,
      queueRef.current.length,
    )
    for (let started = 0; started < toStart; started += 1) {
      inFlightRef.current += 1
      void drain()
    }
    // `chatJids` is watched through its joined key; the activity state is read
    // from the store above, so it must not become a dependency.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chatJidsKey, client, dispatch, store])
}

export default useForeignChatActivity
